import type { VFC } from 'react'
import { unversioned } from '@opallabs/api'
import { Size } from '@opallabs/gem'
import { ChildrenWithInjectedProps } from '@opallabs/lambda'
import { Button, ButtonIconPosition, Icon } from '@opallabs/lapidary'
import { ViewHeader } from '@opallabs/lapidary-unstable'
import type { ViewHeaderActionMenuOption } from '@opallabs/lapidary-unstable'
import { testFeature } from '@opallabs/principles'
import { showPptxToast, trackPptxLaunch } from '../../Editor/pptxExportHelpers'
import { HeaderProps } from './types'

export const Header: VFC<HeaderProps> = ({
  onClickDelete,
  onClickExportPdf,
  onClickPlay,
  onClickShare,
  onNavigateToParent,
  parentTitle,
  presentationId,
  title,
  theme = 'light',
}) => {
  const onClickExportPptx = async () => {
    trackPptxLaunch()

    try {
      await unversioned.createPptxExport({ id: presentationId })
      showPptxToast(
        'Exporting to PowerPoint',
        "When it's ready, it will be in Downloads in the Personal Panel and emailed to you.",
        'success'
      )
    } catch {
      showPptxToast(
        'PowerPoint Export Failed',
        'We could not start your export. Please try again.',
        'error'
      )
    }
  }

  const options: ViewHeaderActionMenuOption[] = []
  if (testFeature('export_capabilities')) {
    options.push({ onClick: onClickExportPdf, title: 'Export to PDF' })
    if (testFeature('pptx_export')) {
      options.push({
        onClick: onClickExportPptx,
        title: 'Export to PowerPoint',
      })
    }
  }

  return (
    <ViewHeader
      actionsChildren={
        <ChildrenWithInjectedProps>
          <Button
            buttonText="Share"
            variant="secondary"
            icon={
              <Icon
                ariaHidden={true}
                height={Size.Icon}
                glyph="gem-share"
                width={Size.Icon}
              />
            }
            iconPosition={ButtonIconPosition.leading}
            name="share-button"
            onClick={onClickShare}
            theme={theme}
          />
          <Button
            buttonText="Play"
            variant="primary"
            icon={
              <Icon
                ariaHidden={true}
                height={Size.Icon}
                glyph="gem-play"
                width={Size.Icon}
              />
            }
            iconPosition={ButtonIconPosition.leading}
            name="play-button"
            onClick={onClickPlay}
            theme={theme}
          />
        </ChildrenWithInjectedProps>
      }
      actionMenuOptions={options}
      onDestroy={onClickDelete}
      onNavigateToParent={onNavigateToParent}
      parentTitle={parentTitle}
      searchable={false}
      theme={theme}
      title={title}
    />
  )
}
Header.displayName = 'Hubble: Header ← PresentationEditor'
